import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'semantic-ui-react'
import InfiniteScroll from 'react-infinite-scroll-component';

import useApiService from '../../services/useApiService';
import withTeams from '../components/with_teams';

import ElementLoader from '../components/element_loader';
import CopyIcon from '../components/copy_icon';
import TrainingCountries from '../components/training_countries';

import { debounce, getCategoryText, getDefaultFrequencyText } from '../../helpers';

import './training_needs_analysis.css'

const TrainingModuleDetails = (props) => {
  const { trainingModule, setChangedTrainingModule, userIsAdmin, countryNames } = props;
  const category = getCategoryText(trainingModule.category);
  const defaultFrequency = getDefaultFrequencyText(trainingModule.default_frequency);
  const shecDiscipline = trainingModule.shec_discipline ? `${trainingModule.shec_discipline}` : '';
  const delivery = trainingModule.delivery ? `${trainingModule.delivery}` : '';
  const runTime = trainingModule.run_time ? `${trainingModule.run_time} hours` : '';
  const shouldBeInBracket = category.length > 0 || defaultFrequency.length > 0 || shecDiscipline.length > 0 || delivery.length > 0 || runTime.length > 0;

  const countryList = trainingModule.region_list || [];
  const countryDesc = countryList.length > 0 ? `: Country Specific` : '';

  const updateTrainingCountries = (newCountryList) => {
    setChangedTrainingModule({...trainingModule, region_list: newCountryList });
  }

  const removeCountry = (country) => {
    const { region_list: countryList } = trainingModule;
    const newCountryList = countryList.filter((c) => c !== country);
    setChangedTrainingModule({...trainingModule, region_list: newCountryList });
  }

  return (
    <React.Fragment>
      {
        trainingModule.link
        ?
        <div className="module-name">
          <a onClick={(evt)=>evt.stopPropagation()} href={trainingModule.link} target='_blank'>{trainingModule.name}</a>
          <CopyIcon value={trainingModule.link}/>
        </div>
        :
        <div className="module-name">
          { trainingModule.name }
        </div>
      }
      <div className="module-details">
        {
          shouldBeInBracket && <span>(</span>
        }
        {
          category.length > 0 && <span className='category'>{category} {countryDesc}</span>
        }
        {
          category.length > 0 && defaultFrequency.length > 0 && <span className='divider'>, </span>
        }
        {
          defaultFrequency.length > 0 && <span className='default-frequency'>{defaultFrequency}</span>
        }
        {
          (category.length > 0 || defaultFrequency.length > 0) && shecDiscipline.length > 0 && <span className='divider'>, </span>
        }
        {
          shecDiscipline.length > 0 && <span className='shec-discipline'>{shecDiscipline}</span>
        }
        {
          (category.length > 0 || defaultFrequency.length > 0 || shecDiscipline.length > 0) && delivery.length > 0 && <span className='divider'>, </span>
        }
        {
          delivery.length > 0 && <span className='delivery'>{delivery}</span>
        }
        {
          (category.length > 0 || defaultFrequency.length > 0 || shecDiscipline.length > 0 || delivery.length > 0) && runTime.length > 0 && <span className='divider'>, </span>
        }
        {
          runTime.length > 0 && <span className='run-time'>{runTime}</span>
        }
        {
          shouldBeInBracket && <span>)</span>
        }
      </div>
      <TrainingCountries
        countryList={countryList}
        countryOptions={countryNames}
        updateCountries={updateTrainingCountries}
        removeCountry={removeCountry}
        canUpdate={userIsAdmin}
      />
    </React.Fragment>
  )
}

const TrainingModuleTeamCell = (props) => {
  const { teamList, team, onClick, isAllStaff, userIsAdmin, hasCountryTag } = props;
  let teamClass = teamList.includes(team) ? "team assigned" : "team";
  teamClass = userIsAdmin ? teamClass : `${teamClass} disabled`;
  teamClass = isAllStaff ? 'team assigned disabled' : teamClass;
  teamClass = hasCountryTag ? `${teamClass} with-country` : teamClass;

  if (isAllStaff) {
    return (
      <td className={teamClass}></td>
    )
  } else if (userIsAdmin) {
    return (
      <td className={teamClass} onClick={onClick}></td>
    )
  } else {
    return (
      <td className={teamClass}></td>
    )
  }
}

function TrainingNeedsAnalysis(props) {
  const { defaultTeamOptions, isLoadingTeams, userIsAdmin } = props;
  const defaultState = { search_term: '', page: 1, per_page: 15, order_by: "category and name" };
  const defaultPayload = { params: defaultState }
  const [ state, setState ] = useState(defaultState);
  const [ trainingModules, setTrainingModules ] = useState([]);

  const [{ isLoading: isLoadingTrainingModules, response: fetchedTrainingModules }, fetchTrainingModules ] 
          = useApiService({ url: `/v2_training_modules`, payload: defaultPayload, onMount: true, onSuccess: refreshTrainingModules, defaultResponse: []});
  const isLoadingData = isLoadingTrainingModules || isLoadingTeams;
  const [ changedTrainingModule, setChangedTrainingModule ] = useState({});

  const [{}, updateTrainingModule ] 
          = useApiService({ url: `/v2_training_modules/${changedTrainingModule.id}`, method: 'put', onSuccess: refreshTrainingModule, defaultResponse: {} });

  const getCountriesPayload = { params: { context: 'regions' } };
  const [{ response: countries }, ] = useApiService({ url: `/tags`, payload: getCountriesPayload, onMount: true, defaultResponse: []});
  const countryNames = countries.map((country) => { return country.name }).sort();
 
  const firstRender = useRef(true);

  const setSearchFilter = (evt) => {
    setState({ ...state, search_term: evt.target.value, page: 1 });
  }

  const searchTrainingModules = (evt) => {
    const key = evt.which;

    if (key == 13) {
      fetchTrainingModules({ params: state });
    }

  }

  const setNewPage = () => {
    if (fetchedTrainingModules.length < state.per_page) return;
    setState({ ...state, page: state.page + 1 })
  }

  const assignOrUnassignTeam = (trainingModule, team) => {
    let newTeamList = [...trainingModule.tag_list];
    if (newTeamList.includes(team)) {
      newTeamList = newTeamList.filter(_team => _team !== team);
    } else {
      newTeamList.push(team);
    }
    setChangedTrainingModule({...trainingModule, tag_list: newTeamList });
  }

  const debouncedFetchTrainingModules = () => {
    debounce(fetchTrainingModules, 1000)({ params: state });
  }

  function refreshTrainingModule (updatedTrainingModule) {
    const { id, tag_list, region_list } = updatedTrainingModule;
    const matchedTrainingModule = trainingModules.find(tm => tm.id === id);
    if (matchedTrainingModule) {
      matchedTrainingModule.tag_list = tag_list;
      matchedTrainingModule.region_list = region_list;
    };
    setTrainingModules([...trainingModules])
  }

  function refreshTrainingModules (fetchedTrainingModules) {
    if (state.page == 1) return setTrainingModules(fetchedTrainingModules);
    setTrainingModules(trainingModules.concat(fetchedTrainingModules))
  }

  useEffect(()=>{
    if (!changedTrainingModule.id) return;
    const { tag_list, region_list } = changedTrainingModule;
    updateTrainingModule({ tag_list, region_list })
  }, [ changedTrainingModule ])

  useEffect(()=>{
    if(firstRender.current) {
      firstRender.current = false;
      return;
    }
    debouncedFetchTrainingModules();
  }, [ state.page, state.search_term ])

  return (
    <div className='tna'>
      <div className='tna-toolbar'>
        <Input
          placeholder='Search Training Module'
          onChange={ setSearchFilter }
          onKeyPress={ searchTrainingModules }
          value={state.searchTerm}
        />
        <span className='tna-color-legend'>
          <label>Color Legends: </label>
          <span className='required'><div className='box'></div><span className='text'>Required</span></span>
          <span className='required-country-specific'><div className='box'></div><span className='text'>Required - Country Specific</span></span>
          <span className='not-required'><div className='box'></div><span className='text'>Not Required</span></span>
        </span>
      </div>
      <InfiniteScroll
        dataLength={trainingModules.length}
        next={setNewPage}
        hasMore={true}
        scrollableTarget="tna-table-container"
      >
        <div id='tna-table-container' className='tna-table-container'>
          <table className='tna-table'>
            <thead>
              <tr>
                <th className="training-details" >Training Module</th>
                {
                  defaultTeamOptions.map((team) => {
                    return <th key={team} className="team">{team}</th>
                  })
                }
              </tr>
            </thead>
            <tbody>
              {
                trainingModules.map((trainingModule) => {
                  const isAllStaff = trainingModule.category === 'all';
                  const hasCountryTag = trainingModule.region_list.length > 0;
                  return (
                    <tr key={trainingModule.id}>
                      <td className="training-details">
                        <TrainingModuleDetails
                          trainingModule={trainingModule}
                          setChangedTrainingModule={setChangedTrainingModule}
                          userIsAdmin={userIsAdmin}
                          countryNames={countryNames}
                        />
                      </td>
                      {
                        defaultTeamOptions.map((team, i) => {
                          return (
                            <TrainingModuleTeamCell
                              key={team}
                              teamList={trainingModule.tag_list}
                              team={team}
                              onClick={()=>assignOrUnassignTeam(trainingModule, team)}
                              isAllStaff={isAllStaff}
                              userIsAdmin={userIsAdmin}
                              hasCountryTag={hasCountryTag}
                            />
                          )
                        })
                      }
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
          <ElementLoader loading={isLoadingData} message="Loading training modules and teams" />
        </div>
      </InfiniteScroll>
    </div>
  )
}

const TrainingNeedsAnalysisWithTeams = withTeams(TrainingNeedsAnalysis);
export default TrainingNeedsAnalysisWithTeams;


// WEBPACK FOOTER //
// ./src/views/training_needs_analysis/training_needs_analysis.js